/**
 * Shift 1's onboarding (plan A7), generation four.
 *
 * Three generations of this layer tried to *detect* the player's understanding — camera
 * within ten degrees of the hostile, the selection being hostile, a magnification change —
 * and each detector was a place for the tutorial to be confidently wrong about what the
 * player was actually seeing. Adam's direction (2026-07-29): the player needs exactly
 * three things — friendlies versus hostiles, HORIZON scans while SCOPE shoots, and which
 * weapon answers which threat. Teach those, clearly, and stop inferring sight.
 *
 * So the steps advance only on facts the sim can state without interpretation: the camera
 * moved at all, a hostile exists on radar, the view changed, a kill was scored, time
 * passed, the tanker arrived. The one safety that survives from the friendly-fire era is
 * a gate, not a detector: the word "fire" never appears while nothing hostile exists, and
 * the instruction names "the red contact" — the colour rule taught two steps earlier —
 * never "the target", which once meant the tanker as far as the whole HUD was concerned.
 *
 * It is a thin prompt layer over the real first shift, skippable step by step, and it
 * never blocks input.
 */

import type { SimSnapshot } from '../sim/types';

export interface TutorialContext {
  snapshot: SimSnapshot;
  view: 'horizon' | 'scope';
  /** Camera yaw in radians, for the "look around" step. */
  cameraYaw: number;
  /** True when the player is on touch, which decides which hint a step shows. */
  touch: boolean;
}

interface Step {
  id: string;
  text: string;
  /**
   * Secondary line, per input device.
   *
   * Two of them, because one line cannot serve both. This used to be a single string
   * carrying every route at once, so a phone was told to click, to press Q and E, and to
   * hold the left mouse button, none of which it has.
   */
  hint?: string;
  touchHint?: string;
  /** The step cannot begin until this is true; `standby` shows meanwhile. */
  ready?: (ctx: TutorialContext) => boolean;
  standby?: string;
  /** True once the step's fact holds. */
  done: (ctx: TutorialContext, state: TutorialState) => boolean;
}

/** Something hostile is alive and actually showing on the radar. */
function hostileOnRadar(ctx: TutorialContext): boolean {
  return ctx.snapshot.vessels.some(
    (v) => v.faction === 'hostile' && v.state !== 'dead' && v.state !== 'sinking'
      && v.detection !== 'undetected',
  );
}

/** The player has killed something. The sim counts it; nothing is inferred. */
function firstBlood(ctx: TutorialContext): boolean {
  return ctx.snapshot.shift.hostilesDestroyed > 0;
}

interface TutorialState {
  startYaw: number;
  /** Sim time when the current step began, for the doctrine card's dwell. */
  startElapsed: number;
}

/**
 * How long the weapon-doctrine card holds before moving on. It is the only step with
 * nothing to do — three facts to read — so it advances on sim time (never wall clock;
 * invariant 6) with the Next button as the fast exit.
 */
const DOCTRINE_DWELL_SECONDS = 12;

const STEPS: Step[] = [
  {
    id: 'look',
    text: 'Drag to look across the sector',
    hint: 'Drag with the mouse',
    touchHint: 'Drag anywhere on the water',
    done: (ctx, st) => Math.abs(ctx.cameraYaw - st.startYaw) > 0.28,
  },
  {
    /*
     * Adam's first lesson, and the whole friendly-fire defence in one rule. It sits on
     * screen through the quiet opening minutes — the tanker arrives green, and the step
     * completes at the exact moment the rule gains a second example: the first red mark,
     * which the radio calls in on the same beat.
     */
    id: 'colours',
    text: 'Green is friendly. Red is hostile.',
    hint: 'Watch the radar — and never fire on green',
    touchHint: 'Watch the radar — and never fire on green',
    done: hostileOnRadar,
  },
  {
    // Adam's second lesson. One sentence, one view change to confirm it landed.
    id: 'scope',
    text: 'Scan from HORIZON — fight from SCOPE',
    hint: 'The SCOPE tab · Tab',
    touchHint: 'The SCOPE button',
    done: (ctx) => ctx.view === 'scope',
  },
  {
    /*
     * The first thing the player is ever told to shoot is named by colour, tying the
     * instruction to the rule from two steps back rather than to whatever the HUD
     * happens to have selected. Completing on the sim's own kill counter means the step
     * can never congratulate anyone for shooting at the sea — or at the tanker.
     *
     * `ready` also accepts an existing kill so a player who found the trigger early is
     * not parked on the standby line waiting for a target they already dealt with.
     */
    id: 'fire',
    text: 'Open fire on the red contact — lead it, shells take seconds to arrive',
    hint: 'Hold the left mouse button',
    touchHint: 'Hold FIRE — you can still drag to track',
    ready: (ctx) => hostileOnRadar(ctx) || firstBlood(ctx),
    standby: 'Hold fire — nothing hostile on the radar',
    done: firstBlood,
  },
  {
    // Adam's third lesson, in his words. Shown in the lull after first blood.
    id: 'weapons',
    text: 'Guns for small boats. Torpedoes: slow but guided. Depth charges: for submerged enemies.',
    hint: 'Switch on the weapon cards · keys 1 2 3',
    touchHint: 'Switch on the weapon cards',
    done: (ctx, st) => ctx.snapshot.shift.elapsed - st.startElapsed > DOCTRINE_DWELL_SECONDS,
  },
  {
    /*
     * Ends on the tanker actually arriving rather than on a view change — the shift's
     * own climax is the right ending for a layer that is meant to be the shift.
     */
    id: 'protect',
    text: 'Back to HORIZON — see Ardent safely in',
    hint: 'She is safe once inside the ring on the radar',
    touchHint: 'She is safe once inside the ring on the radar',
    done: (ctx) => ctx.view === 'horizon' && ctx.snapshot.shift.arrivalsSafe > 0,
  },
];

/**
 * Exported for tests only. The step logic is pure — predicates over a snapshot — and the
 * test pins the ordering, the fire gate, and the vocabulary Adam struck from the script.
 */
export { STEPS as TUTORIAL_STEPS };

export class Tutorial {
  private readonly root: HTMLDivElement;
  private readonly textEl: HTMLElement;
  private readonly hintEl: HTMLElement;
  private readonly stepEl: HTMLElement;

  private index = 0;
  private active = false;
  private state: TutorialState = { startYaw: 0, startElapsed: 0 };
  /** Hold each completed step on screen briefly so the player sees it tick over. */
  private holdTimer = 0;
  /** True while a step is showing its standby line rather than its instruction. */
  private showingStandby = false;

  /**
   * The last context seen, so the Next button can re-baseline the step it moves to.
   *
   * A button press happens between frames and has no context of its own; without this,
   * skipping onto the "look" or "weapons" steps would measure them against a stale yaw
   * or start time and complete them instantly.
   */
  private lastCtx: TutorialContext | null = null;

  constructor(parent: HTMLElement, onSkip: () => void) {
    this.root = document.createElement('div');
    this.root.id = 'tutorial';
    this.root.innerHTML = `
      <div class="tutStep mono"></div>
      <div class="tutText"></div>
      <div class="tutHint"></div>
      <button class="tutNext" type="button">Next</button>
    `;
    parent.appendChild(this.root);

    this.stepEl = this.root.querySelector('.tutStep') as HTMLElement;
    this.textEl = this.root.querySelector('.tutText') as HTMLElement;
    this.hintEl = this.root.querySelector('.tutHint') as HTMLElement;
    /*
     * Next moves on by one, on Adam's direction — it replaces a Skip that threw away the
     * whole tutorial. One button that means "I have this one" is far more useful than one
     * that means "I have all of these", and it is also what makes the passive steps
     * fine: a player who does not want to sit through a lesson can move past it without
     * abandoning the steps they have not seen yet.
     */
    this.root.querySelector('.tutNext')?.addEventListener('click', () => {
      this.advance();
      if (!this.active) onSkip();
    });
  }

  /** Move to the next step immediately, or finish if this was the last. */
  private advance(): void {
    this.holdTimer = 0;
    this.index++;
    if (this.index >= STEPS.length) { this.finish(); return; }
    if (this.lastCtx) this.rebaseline(this.lastCtx);
    this.render();
  }

  /** Measure the incoming step from where the player is now, not where they started. */
  private rebaseline(ctx: TutorialContext): void {
    this.state.startYaw = ctx.cameraYaw;
    this.state.startElapsed = ctx.snapshot.shift.elapsed;
  }

  get isActive(): boolean {
    return this.active;
  }

  start(ctx: TutorialContext): void {
    this.active = true;
    this.index = 0;
    this.holdTimer = 0;
    this.showingStandby = false;
    this.lastCtx = ctx;
    this.state = {
      startYaw: ctx.cameraYaw,
      startElapsed: ctx.snapshot.shift.elapsed,
    };
    this.root.classList.add('show');
    this.render(ctx);
  }

  stop(): void {
    this.active = false;
    this.root.classList.remove('show');
  }

  update(ctx: TutorialContext, dt: number): void {
    if (!this.active) return;
    this.lastCtx = ctx;

    if (this.holdTimer > 0) {
      this.holdTimer -= dt;
      if (this.holdTimer <= 0) {
        this.index++;
        if (this.index >= STEPS.length) { this.finish(); return; }
        this.rebaseline(ctx);
        this.render();
      }
      return;
    }

    const step = STEPS[this.index];
    if (!step) { this.finish(); return; }

    // A step that is not ready shows its standby line and keeps re-baselining, so it is
    // measured from when it actually begins rather than from when it was reached.
    if (step.ready && !step.ready(ctx)) {
      this.rebaseline(ctx);
      this.renderStandby(step, ctx);
      return;
    }
    if (this.showingStandby) this.render(ctx);

    if (step.done(ctx, this.state)) {
      this.root.classList.add('done');
      this.holdTimer = 1.1;
    }
  }

  private finish(): void {
    this.textEl.textContent = 'You have the watch.';
    this.hintEl.textContent = '';
    this.stepEl.textContent = '';
    this.root.classList.add('done');
    window.setTimeout(() => this.stop(), 2600);
    this.active = false;
  }

  private render(ctx?: TutorialContext): void {
    const step = STEPS[this.index];
    if (!step) return;
    this.showingStandby = false;
    this.root.classList.remove('done', 'waiting');
    this.stepEl.textContent = `${this.index + 1} / ${STEPS.length}`;
    this.textEl.textContent = step.text;
    this.hintEl.textContent = this.hintFor(step, ctx ?? this.lastCtx);
  }

  /** The holding pattern: same slot, different line, and no step number ticking over. */
  private renderStandby(step: Step, ctx: TutorialContext): void {
    if (this.showingStandby) return;
    this.showingStandby = true;
    this.root.classList.remove('done');
    this.root.classList.add('waiting');
    this.stepEl.textContent = `${this.index + 1} / ${STEPS.length}`;
    this.textEl.textContent = step.standby ?? step.text;
    this.hintEl.textContent = this.hintFor(step, ctx);
  }

  /** The route that exists on the device in the player's hands. */
  private hintFor(step: Step, ctx: TutorialContext | null): string {
    if (ctx?.touch) return step.touchHint ?? '';
    return step.hint ?? '';
  }

  dispose(): void {
    this.root.remove();
  }
}
